import React, { useCallback, useState } from 'react';
import { Clock, Globe, Calendar, Search, Save, RefreshCw } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент основной формы конвертера часовых поясов
 */
const ConverterForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  setConvertedTime,
  setTimeDifference,
  setWorldClocks,
  russianCities,
  worldCities
}) => {
  // Состояние для сохранения формы
  const [saveTimeZoneName, setSaveTimeZoneName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };
      
      // Если выбирается город, автоматически устанавливаем его часовой пояс
      if (name === 'sourceCity') {
        const city = [...russianCities, ...worldCities].find(c => c.id === value);
        if (city) {
          newState.sourceTimezone = city.timezone;
        }
      }
      
      if (name === 'targetCity') {
        const city = [...russianCities, ...worldCities].find(c => c.id === value);
        if (city) {
          newState.targetTimezone = city.timezone;
        }
      }
      
      // При изменении вида расчета сбрасываем результаты
      if (name === 'view') {
        setConvertedTime(null);
        setTimeDifference(null);
        setWorldClocks([]);
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [russianCities, worldCities, errors, setErrors, setFormData, setConvertedTime, setTimeDifference, setWorldClocks]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      sourceCity, targetCity, date, time, view
    } = formData;

    // Проверка городов
    if (!sourceCity) newErrors.sourceCity = 'Выберите город отправления.';
    if (view !== 'worldclock' && !targetCity) newErrors.targetCity = 'Выберите город назначения.';
    
    // Проверка даты и времени
    if (!date) newErrors.date = 'Выберите дату.';
    if (!time) newErrors.time = 'Введите время.';

    // Проверяем валидность даты и времени
    try {
      const dateObj = new Date(`${date}T${time}`);
      if (isNaN(dateObj.getTime())) {
        if (!newErrors.date) newErrors.date = 'Некорректная дата.';
        if (!newErrors.time) newErrors.time = 'Некорректное время.';
      }
    } catch (e) {
      if (!newErrors.date) newErrors.date = 'Некорректная дата.';
      if (!newErrors.time) newErrors.time = 'Некорректное время.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция конвертации времени
  const convertTime = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    
    // Очищаем предыдущие результаты
    setConvertedTime(null);
    setTimeDifference(null);
    setWorldClocks([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        const { sourceCity, targetCity, sourceTimezone, targetTimezone, date, time, view, showSeconds, includeDate } = formData;
        
        // Получаем информацию о выбранных городах
        const sourceCityInfo = [...russianCities, ...worldCities].find(c => c.id === sourceCity);
        const targetCityInfo = [...russianCities, ...worldCities].find(c => c.id === targetCity);
        
        // Создаем метку времени в исходном часовом поясе
        const inputTime = new Date(`${date}T${time}`);
        
        if (view === 'converter') {
          // Конвертация времени между двумя часовыми поясами
          const sourceOptions = {
            timeZone: sourceTimezone,
            hour: '2-digit',
            minute: '2-digit',
            ...(showSeconds ? { second: '2-digit' } : {}),
            hour12: false
          };
          
          const targetOptions = {
            timeZone: targetTimezone,
            hour: '2-digit',
            minute: '2-digit',
            ...(showSeconds ? { second: '2-digit' } : {}),
            hour12: false
          };
          
          // Форматируем дату, если нужно
          const dateOptions = {
            timeZone: sourceTimezone,
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          };
          
          const targetDateOptions = {
            timeZone: targetTimezone,
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          };
          
          // Получаем смещение часовых поясов для показа
          const sourceDate = new Intl.DateTimeFormat('ru-RU', dateOptions).format(inputTime);
          const targetDate = new Intl.DateTimeFormat('ru-RU', targetDateOptions).format(inputTime);
          
          const sourceTime = new Intl.DateTimeFormat('ru-RU', sourceOptions).format(inputTime);
          const targetTime = new Intl.DateTimeFormat('ru-RU', targetOptions).format(inputTime);
          
          // Рассчитываем разницу во времени
          const sourceOffsetMinutes = getTimezoneOffsetInMinutes(inputTime, sourceTimezone);
          const targetOffsetMinutes = getTimezoneOffsetInMinutes(inputTime, targetTimezone);
          const differenceMinutes = targetOffsetMinutes - sourceOffsetMinutes;
          
          // Форматируем разницу для отображения
          const hours = Math.abs(Math.floor(differenceMinutes / 60));
          const minutes = Math.abs(differenceMinutes % 60);
          const sign = differenceMinutes >= 0 ? '+' : '-';
          const formattedDifference = `${sign}${hours}${minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : ''} ч`;
          
          // Форматируем смещения для отображения
          const formatOffset = (offsetMinutes) => {
            const hours = Math.floor(Math.abs(offsetMinutes) / 60);
            const minutes = Math.abs(offsetMinutes) % 60;
            const sign = offsetMinutes >= 0 ? '+' : '-';
            return `${sign}${hours}${minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : ''}`;
          };
          
          setConvertedTime({
            sourceTime,
            targetTime,
            sourceDate: includeDate ? sourceDate : null,
            targetDate: includeDate ? targetDate : null,
            difference: formattedDifference,
            sourceOffset: formatOffset(sourceOffsetMinutes),
            targetOffset: formatOffset(targetOffsetMinutes)
          });
        } else if (view === 'worldclock') {
          // Мировые часы - показываем время в нескольких городах одновременно
          const allCities = [...russianCities, ...worldCities];
          const clocks = allCities.map(city => {
            const options = {
              timeZone: city.timezone,
              hour: '2-digit',
              minute: '2-digit',
              ...(showSeconds ? { second: '2-digit' } : {}),
              hour12: false
            };
            
            const dateOptions = {
              timeZone: city.timezone,
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            };
            
            const currentTime = new Intl.DateTimeFormat('ru-RU', options).format(inputTime);
            const currentDate = new Intl.DateTimeFormat('ru-RU', dateOptions).format(inputTime);
            
            // Получаем смещение
            const offsetMinutes = getTimezoneOffsetInMinutes(inputTime, city.timezone);
            const formattedOffset = formatOffset(offsetMinutes);
            
            return {
              ...city,
              currentTime,
              currentDate: includeDate ? currentDate : null,
              utcOffset: formattedOffset
            };
          });
          
          setWorldClocks(clocks);
        } else if (view === 'difference') {
          // Расчет разницы во времени между городами
          const sourceOffsetMinutes = getTimezoneOffsetInMinutes(inputTime, sourceTimezone);
          const targetOffsetMinutes = getTimezoneOffsetInMinutes(inputTime, targetTimezone);
          const differenceMinutes = targetOffsetMinutes - sourceOffsetMinutes;
          
          // Форматируем разницу для отображения
          const hours = Math.abs(Math.floor(differenceMinutes / 60));
          const minutes = Math.abs(differenceMinutes % 60);
          const sign = differenceMinutes >= 0 ? '+' : '-';
          
          // Рассчитываем время прилета
          const arrivalTime = new Date(inputTime.getTime() + differenceMinutes * 60 * 1000);
          
          // Получаем конкретные данные, сколько времени будет в другом городе
          // в момент отправления из исходного города
          const sourceOptions = {
            timeZone: sourceTimezone,
            hour: '2-digit',
            minute: '2-digit',
            ...(showSeconds ? { second: '2-digit' } : {}),
            hour12: false
          };
          
          const targetOptions = {
            timeZone: targetTimezone,
            hour: '2-digit',
            minute: '2-digit',
            ...(showSeconds ? { second: '2-digit' } : {}),
            hour12: false
          };
          
          const sourceTime = new Intl.DateTimeFormat('ru-RU', sourceOptions).format(inputTime);
          const targetTime = new Intl.DateTimeFormat('ru-RU', targetOptions).format(inputTime);
          
          // Генерируем примеры времени с разницей
          const exampleTimes = generateExampleTimes(inputTime, sourceTimezone, targetTimezone, showSeconds);
          
          setTimeDifference({
            hours,
            minutes,
            sign,
            formattedDifference: `${sign}${hours}${minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : ''} ч`,
            sourceTime,
            targetTime,
            sourceCityInfo,
            targetCityInfo,
            exampleTimes
          });
        }
        
      } catch (error) {
        console.error("Ошибка конвертации:", error);
        setErrors({ general: `Произошла ошибка при конвертации: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setConvertedTime, setTimeDifference, setWorldClocks, setIsCalculating, russianCities, worldCities]);

  // Вспомогательная функция для получения смещения часового пояса в минутах
  const getTimezoneOffsetInMinutes = (date, timezone) => {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    return (tzDate - utcDate) / 60000;
  };

  // Вспомогательная функция для форматирования смещения часового пояса
  const formatOffset = (offsetMinutes) => {
    const hours = Math.floor(Math.abs(offsetMinutes) / 60);
    const minutes = Math.abs(offsetMinutes) % 60;
    const sign = offsetMinutes >= 0 ? '+' : '-';
    return `${sign}${hours}${minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : ''}`;
  };

  // Генерация примеров времени для вкладки "Разница во времени"
  const generateExampleTimes = (baseDate, sourceTimezone, targetTimezone, showSeconds) => {
    const examples = [];
    const sourceOptions = {
      timeZone: sourceTimezone,
      hour: '2-digit',
      minute: '2-digit',
      ...(showSeconds ? { second: '2-digit' } : {}),
      hour12: false
    };
    
    const targetOptions = {
      timeZone: targetTimezone,
      hour: '2-digit',
      minute: '2-digit',
      ...(showSeconds ? { second: '2-digit' } : {}),
      hour12: false
    };
    
    // Генерируем примеры для каждых 3 часов
    for (let i = 0; i < 24; i += 3) {
      const exampleDate = new Date(baseDate);
      exampleDate.setHours(i, 0, 0, 0);
      
      examples.push({
        sourceTime: new Intl.DateTimeFormat('ru-RU', sourceOptions).format(exampleDate),
        targetTime: new Intl.DateTimeFormat('ru-RU', targetOptions).format(exampleDate)
      });
    }
    
    return examples;
  };

  // Сброс формы
  const resetForm = () => {
    setFormData({
      sourceCity: 'Moscow',
      sourceTimezone: 'Europe/Moscow',
      targetCity: 'New_York',
      targetTimezone: 'America/New_York',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      includeDate: true,
      showSeconds: false,
      view: 'converter',
    });
    setConvertedTime(null);
    setTimeDifference(null);
    setWorldClocks([]);
    setErrors({});
    setSaveTimeZoneName('');
    setShowSaveDialog(false);
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    convertTime();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Globe className="w-6 h-6 text-purple-500" />
          Конвертер часовых поясов
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Выбор типа расчета */}
        <div className="mb-6">
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Выберите необходимую функцию
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
            <label className={`
              p-3 border rounded-lg cursor-pointer flex flex-col items-center justify-center text-center transition-colors
              ${formData.view === 'converter' 
                ? 'bg-purple-50 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
              }
            `}>
              <input
                type="radio"
                name="view"
                value="converter"
                checked={formData.view === 'converter'}
                onChange={handleInputChange}
                className="sr-only"
              />
              <Clock className="w-6 h-6 mb-2" />
              <span className="font-medium">Конвертер времени</span>
            </label>
            
            <label className={`
              p-3 border rounded-lg cursor-pointer flex flex-col items-center justify-center text-center transition-colors
              ${formData.view === 'worldclock' 
                ? 'bg-purple-50 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
              }
            `}>
              <input
                type="radio"
                name="view"
                value="worldclock"
                checked={formData.view === 'worldclock'}
                onChange={handleInputChange}
                className="sr-only"
              />
              <Globe className="w-6 h-6 mb-2" />
              <span className="font-medium">Мировые часы</span>
            </label>
            
            <label className={`
              p-3 border rounded-lg cursor-pointer flex flex-col items-center justify-center text-center transition-colors
              ${formData.view === 'meeting' 
                ? 'bg-purple-50 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
              }
            `}>
              <input
                type="radio"
                name="view"
                value="meeting"
                checked={formData.view === 'meeting'}
                onChange={handleInputChange}
                className="sr-only"
              />
              <Calendar className="w-6 h-6 mb-2" />
              <span className="font-medium">Планировщик встреч</span>
            </label>
            
            <label className={`
              p-3 border rounded-lg cursor-pointer flex flex-col items-center justify-center text-center transition-colors
              ${formData.view === 'difference' 
                ? 'bg-purple-50 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
              }
            `}>
              <input
                type="radio"
                name="view"
                value="difference"
                checked={formData.view === 'difference'}
                onChange={handleInputChange}
                className="sr-only"
              />
              <Search className="w-6 h-6 mb-2" />
              <span className="font-medium">Разница во времени</span>
            </label>
          </div>
        </div>

        {/* Выбор городов */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Город отправления */}
          <div>
            <label htmlFor="sourceCity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Clock className="w-4 h-4 mr-2 text-purple-500" />
              {formData.view === 'worldclock' ? 'Город для отображения' : 'Город отправления'}
            </label>
            <select
              id="sourceCity"
              name="sourceCity"
              value={formData.sourceCity}
              onChange={handleInputChange}
              className={`form-select w-full ${errors.sourceCity ? 'input-error' : ''}`}
            >
              <optgroup label="Россия">
                {russianCities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name} (UTC{city.utcOffset})
                  </option>
                ))}
              </optgroup>
              <optgroup label="Мир">
                {worldCities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name} (UTC{city.utcOffset})
                  </option>
                ))}
              </optgroup>
            </select>
            {errors.sourceCity && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.sourceCity}
              </p>
            )}
          </div>

          {/* Город назначения (не показывается для мировых часов) */}
          {formData.view !== 'worldclock' && (
            <div>
              <label htmlFor="targetCity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <Clock className="w-4 h-4 mr-2 text-purple-500" />
                Город назначения
              </label>
              <select
                id="targetCity"
                name="targetCity"
                value={formData.targetCity}
                onChange={handleInputChange}
                className={`form-select w-full ${errors.targetCity ? 'input-error' : ''}`}
              >
                <optgroup label="Россия">
                  {russianCities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name} (UTC{city.utcOffset})
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Мир">
                  {worldCities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name} (UTC{city.utcOffset})
                    </option>
                  ))}
                </optgroup>
              </select>
              {errors.targetCity && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.targetCity}
                </p>
              )}
            </div>
          )}

          {/* Дата и время */}
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Calendar className="w-4 h-4 mr-2 text-purple-500" />
              Дата
            </label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.date ? 'input-error' : ''}`}
            />
            {errors.date && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.date}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="time" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Clock className="w-4 h-4 mr-2 text-purple-500" />
              Время
            </label>
            <input
              type="time"
              id="time"
              name="time"
              value={formData.time}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.time ? 'input-error' : ''}`}
            />
            {errors.time && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.time}
              </p>
            )}
          </div>
        </div>

        {/* Дополнительные опции */}
        <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            Параметры отображения
          </h3>
          
          <div className="flex flex-wrap items-center gap-4">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="includeDate"
                checked={formData.includeDate}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Показывать дату
              </span>
            </label>
            
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="showSeconds"
                checked={formData.showSeconds}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Показывать секунды
              </span>
            </label>
          </div>
        </div>

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Clock className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Вычисляем...' : 'Рассчитать время'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default ConverterForm;